import React, { useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Typography,
  Paper,
  Divider,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
  FileDownloadOutlinedIcon,
  ExpandMoreIcon,
  CircularProgress
} from '@esgian/esgianui';
import OngoingBulkSearchTable from './OngoingBulkSearchTable';
import OngoingBulkSearchCards from './OngoingBulkSearchCards';
import { getVoyageSearchDetails } from '@api';
import { useSegment, useLookupOperatorVesselHierarchyQuery } from '@hooks';
import moment from 'moment';
import * as XLSX from 'xlsx';
import { useDispatch } from 'react-redux';
import {
  updateImoStartDateMap,
  setBulkSearchMode
} from '@store/features/filters/VoyageAnalyticsPage/OngoingVoyage/OngoingVoyagesSlice';

const OngoingBulkSearchSection = ({
  onSearchResults,
  voyageData: initialVoyageData = [],
  onDataChange,
  displayMode: initialDisplayMode = 'table',
  onDisplayModeChange
}) => {
  const [voyageData, setVoyageData] = useState(initialVoyageData);
  const [isSearching, setIsSearching] = useState(false);
  const [isReadingFile, setIsReadingFile] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [displayMode, setDisplayMode] = useState(initialDisplayMode);
  const [expanded, setExpanded] = useState(false);
  const [error, setError] = useState(null);
  const [fileName, setFileName] = useState(null);
  const { segment } = useSegment();
  const lookupOperatorVesselHierarchyQuery = useLookupOperatorVesselHierarchyQuery();
  const dispatch = useDispatch();

  // Update local state when props change
  React.useEffect(() => {
    if (initialVoyageData.length > 0) {
      setVoyageData(initialVoyageData);
    }
  }, [initialVoyageData]);

  React.useEffect(() => {
    setDisplayMode(initialDisplayMode);
  }, [initialDisplayMode]);

  // Set bulk search mode on component mount and cleanup on unmount
  React.useEffect(() => {
    dispatch(setBulkSearchMode(true));

    return () => {
      dispatch(setBulkSearchMode(false));
    };
  }, [dispatch]);

  // Flatten vessel data for searching
  const vessels = useMemo(() => {
    if (lookupOperatorVesselHierarchyQuery.isFetching || !lookupOperatorVesselHierarchyQuery.data) {
      return [];
    }

    const operatorHierarchy = [...lookupOperatorVesselHierarchyQuery.data];
    return operatorHierarchy.reduce((acc, operator) => {
      const childrenWithOperator = operator.children.map((vessel) => ({
        ...vessel,
        operatorName: operator.name,
        operatorId: operator.id,
        imo: vessel.id // The vessel ID is the IMO
      }));
      return [...acc, ...childrenWithOperator];
    }, []);
  }, [lookupOperatorVesselHierarchyQuery.data]);

  const handleDataExtracted = useCallback(
    (data) => {
      const resolvedData = resolveEntities(data);
      setVoyageData(resolvedData);
      if (onDataChange) {
        onDataChange(resolvedData);
      }
    },
    [onDataChange, onDisplayModeChange]
  );

  const handleVoyageDataChange = useCallback(
    (data) => {
      setVoyageData(data);
      if (onDataChange) {
        onDataChange(data);
      }
    },
    [onDataChange]
  );

  const findVesselByNameOrImo = (searchTerm) => {
    if (!searchTerm) return null;

    const vesselByName = vessels.find(
      (vessel) => vessel.name.toLowerCase() === searchTerm.toString().toLowerCase()
    );
    const vesselByImo = vessels.find((vessel) => vessel.imo.toString() === searchTerm);

    if (vesselByName) return vesselByName;
    if (vesselByImo) return vesselByImo;

    return null;
  };

  const resolveEntities = (data) => {
    const resolvedData = [...data];

    for (let i = 0; i < resolvedData.length; i++) {
      const item = resolvedData[i];

      // Resolve vessel using offline data
      if (item.vesselName || item.vesselImo) {
        const searchTerm = item.vesselImo || item.vesselName;
        const matchedVessel = findVesselByNameOrImo(searchTerm);

        if (matchedVessel) {
          resolvedData[i].resolvedVessel = {
            imo: matchedVessel.imo,
            name: matchedVessel.name,
            operatorId: matchedVessel.operatorId,
            operatorName: matchedVessel.operatorName
          };
        }
      }
    }

    return resolvedData;
  };

  const handleSearch = async () => {
    if (voyageData.length === 0) return;

    setIsSearching(true);
    setHasSearched(true);

    try {
      const resolvedData = resolveEntities(voyageData);
      setVoyageData(resolvedData);
      if (onDataChange) {
        onDataChange(resolvedData);
      }

      const allResults = [];

      // Create IMO to startDate mapping
      const imoStartDateMapping = {};

      resolvedData
        .filter((item) => item.resolvedVessel && moment(item.startDate).isValid())
        .forEach((item) => {
          // Check if startDate is before 100 days ago
          const hundredDaysAgo = moment().subtract(100, 'days');
          const startDate = moment(item.startDate);
          const finalDate = startDate.isBefore(hundredDaysAgo) ? hundredDaysAgo : startDate;

          imoStartDateMapping[item.resolvedVessel.imo] = finalDate.format('YYYY-MM-DD HH:mm:ss');
        });

      // Update the Redux store with IMO to startDate mapping
      dispatch(updateImoStartDateMap(imoStartDateMapping));

      // Create an array of promises for all valid search items
      const searchPromises = resolvedData
        .filter((item) => item.resolvedVessel && moment(item.startDate).isValid())
        .map((item) => {
          const searchBody = {
            imos: [item.resolvedVessel.imo],
            operatorIds: [item.resolvedVessel.operatorId],
            operatorTypeId: 0,
            segmentTypeId: segment.id,
            startDate: moment(item.startDate).format('YYYY-MM-DD HH:mm:ss'),
            endDate: null,
            minTransitTime: null,
            maxTransitTime: null
          };

          return getVoyageSearchDetails(searchBody)
            .then((response) => {
              if (response && response.voyageSearchResults) {
                return response.voyageSearchResults;
              }
              return [];
            })
            .catch((error) => {
              console.error('Error performing search for item:', item, error);
              return [];
            });
        });

      // Execute all promises in parallel and merge results
      const searchResults = await Promise.all(searchPromises);
      allResults.push(...searchResults.flat());

      // Deduplicate results based on voyageId
      const uniqueResults = [];
      const voyageIds = new Set();

      for (const result of allResults) {
        if (!voyageIds.has(result.imo)) {
          voyageIds.add(result.imo);
          uniqueResults.push(result);
        }
      }

      // Send results to parent component to display in the shared results view
      onSearchResults(uniqueResults);
    } catch (error) {
      console.error('Error performing bulk search:', error);
    } finally {
      setIsSearching(false);
    }
  };

  const handleAccordionChange = () => {
    setExpanded(!expanded);
  };

  const toggleDisplayMode = () => {
    const newMode = displayMode === 'cards' ? 'table' : 'cards';
    setDisplayMode(newMode);
    if (onDisplayModeChange) {
      onDisplayModeChange(newMode);
    }
  };

  const generateSampleFile = () => {
    const imoList = [9343261, 9395355, 9395343, 9443566, 9372339, 9846213, 9615391, 8820858];
    // Create sample data using IMOs from the list with random dates in the past
    const sampleData = imoList.slice(0, 80).map((imo) => {
      // Generate random date between 1-12 months ago
      const randomMonthsAgo = Math.floor(Math.random() * 12) + 1;
      const randomDaysAgo = Math.floor(Math.random() * 28) + 1;
      const randomDate = moment()
        .subtract(randomMonthsAgo, 'months')
        .subtract(randomDaysAgo, 'days')
        .format('YYYY-MM-DD');
      return {
        vessel: String(imo),
        startDate: randomDate
      };
    });

    // Add a couple of named vessels as examples
    sampleData.push(
      {
        vessel: 'Atlas Leader',
        startDate: moment().subtract(3, 'months').format('YYYY-MM-DD')
      },
      {
        vessel: 'Glovis Chorus',
        startDate: moment().subtract(5, 'months').format('YYYY-MM-DD')
      }
    );

    const workbook = XLSX.utils.book_new();
    const worksheet = XLSX.utils.json_to_sheet(sampleData);
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Voyage Search');

    XLSX.writeFile(workbook, 'ongoing_voyage_search_template.xlsx');
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setFileName(file.name);
    setIsReadingFile(true);
    setError(null);

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const firstSheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[firstSheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);

        // Validate and transform the data
        const processedData = jsonData.map((row, index) => {
          const isVesselValid = row.vessel && row.vessel !== '';
          const isDateValid = row.startDate && row.startDate !== '';

          if (!isVesselValid) {
            console.warn(`Row ${index + 1}: Missing vessel information`);
          }
          if (!isDateValid) {
            console.warn(`Row ${index + 1}: Missing start date`);
          }

          // Is the vessel an IMO (numeric)?
          const isImo = /^\d+$/.test(row.vessel || '');

          // Process and format dates to ensure consistent YYYY-MM-DD format
          let startDate = '';

          // Check if the date is a serial number (Excel date)
          if (typeof row.startDate === 'number') {
            try {
              // Explicitly convert Excel serial date to JS Date object
              const jsDate = XLSX.SSF.parse_date_code(row.startDate);
              // Format using explicit format to avoid ambiguity
              const dateObj = new Date(jsDate.y, jsDate.m - 1, jsDate.d);
              const momentDate = moment(dateObj);
              if (momentDate.isValid()) {
                startDate = momentDate.format('YYYY-MM-DD');
              }
            } catch (error) {
              console.warn(`Unable to parse numeric date in row ${index + 1}:`, error);
            }
          } else if (row.startDate && typeof row.startDate === 'string') {
            // Try to parse string dates in various formats
            const parsedDate = moment(row.startDate, [
              'MM/DD/YYYY',
              'DD/MM/YYYY',
              'YYYY-MM-DD',
              'M/D/YYYY',
              'D/M/YYYY'
            ]);
            if (parsedDate.isValid()) {
              startDate = parsedDate.format('YYYY-MM-DD');
            }
          }

          return {
            id: index,
            vesselImo: isImo ? row.vessel : null,
            vesselName: !isImo ? row.vessel : null,
            startDate: startDate,
            isValid: isVesselValid && isDateValid,
            resolvedVessel: null
          };
        });

        setError(null);
        handleDataExtracted(processedData);
      } catch (error) {
        console.error('Error processing Excel file:', error);
        setError(error.message || 'Failed to process Excel file');
      } finally {
        setIsReadingFile(false);
      }
    };

    reader.onerror = () => {
      setError('Failed to read file');
      setFileName(null);
      setIsReadingFile(false);
    };

    reader.readAsArrayBuffer(file);
  };

  const isLoading = useMemo(() => {
    return isSearching || lookupOperatorVesselHierarchyQuery.isFetching || isReadingFile;
  }, [isSearching, lookupOperatorVesselHierarchyQuery.isFetching, isReadingFile]);

  return (
    <Paper elevation={0} sx={{ width: '100%' }}>
      <Box sx={{ p: 2, pb: 1 }}>
        <Typography variant="h6" gutterBottom>
          Bulk Search
        </Typography>
        <Typography variant="body2" color="text.secondary" paragraph>
          Upload an Excel file with vessel information or use the template to get started.
        </Typography>
      </Box>

      <Box sx={{ px: 2, pb: 2 }}>
        <Box
          sx={{
            border: '1px dashed',
            borderColor: 'divider',
            borderRadius: '4px',
            bgcolor: 'background.paper'
          }}>
          <Box sx={{ p: 2 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'flex-start'
              }}>
              <Box sx={{ flex: 1 }}>
                <Typography variant="subtitle1" sx={{ mb: 0.5 }}>
                  Upload an Excel file with the required columns
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Required columns: vessel, startDate
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <Button
                  variant="outlined"
                  size="medium"
                  startIcon={<FileDownloadOutlinedIcon color="primary" />}
                  onClick={generateSampleFile}
                  disabled={isReadingFile}>
                  Download Template
                </Button>

                <Box>
                  <input
                    accept=".xlsx,.xls"
                    id="upload-voyage-data"
                    type="file"
                    style={{ display: 'none' }}
                    onChange={handleFileUpload}
                    disabled={isReadingFile}
                  />
                  <label htmlFor="upload-voyage-data">
                    <Button
                      variant="contained"
                      color="primary"
                      component="span"
                      startIcon={
                        isReadingFile ? (
                          <CircularProgress size={16} color="inherit" />
                        ) : (
                          <FileDownloadOutlinedIcon
                            style={{ transform: 'rotate(180deg)' }}
                            color="inherit"
                          />
                        )
                      }
                      disabled={isReadingFile}
                      size="medium">
                      {isReadingFile ? 'Processing...' : 'Upload Excel'}
                    </Button>
                  </label>
                </Box>
              </Box>
            </Box>
            {fileName && !isReadingFile && (
              <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>
                File: {fileName}
              </Typography>
            )}
            {isReadingFile && (
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  mt: 2,
                  color: 'text.secondary'
                }}>
                <CircularProgress size={20} sx={{ mr: 1 }} />
                <Typography variant="body2">Reading file data...</Typography>
              </Box>
            )}
            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}
          </Box>

          <Accordion expanded={expanded} onChange={handleAccordionChange}>
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{ borderTop: '1px dashed', borderColor: 'divider' }}>
              <Typography variant="body2" color="primary">
                View detailed instructions
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
              <Box component="ul" sx={{ pl: 2, mb: 2, mt: 1 }}>
                <Typography component="li" variant="body2">
                  <b>vessel</b> - Vessel name or IMO number
                </Typography>
                <Typography component="li" variant="body2">
                  <b>startDate</b> - Start date in YYYY-MM-DD format
                </Typography>
              </Box>

              <Typography variant="body2" sx={{ mb: 1 }}>
                <b>Note:</b> The system will try to match your vessel entries with known vessels in
                the system. Use a vessel name or IMO number for the best results.
              </Typography>
            </AccordionDetails>
          </Accordion>
        </Box>
      </Box>

      {isReadingFile && voyageData.length === 0 && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            p: 4
          }}>
          <CircularProgress size={60} />
        </Box>
      )}

      {voyageData.length > 0 && !isReadingFile && (
        <>
          <Divider />
          <Box sx={{ p: 2 }}>
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2
              }}>
              <Typography variant="subtitle1">Search Data ({voyageData.length} items)</Typography>
              <Typography
                variant="body2"
                sx={{
                  cursor: 'pointer',
                  textDecoration: 'underline',
                  '&:hover': { color: 'primary.main' }
                }}
                onClick={toggleDisplayMode}>
                Switch to {displayMode === 'cards' ? 'table' : 'card'} view
              </Typography>
            </Box>

            {displayMode === 'cards' ? (
              <OngoingBulkSearchCards
                voyageData={voyageData}
                vessels={vessels}
                onDataChange={handleVoyageDataChange}
                onSearch={handleSearch}
                isSearching={isLoading}
                hasSearched={hasSearched}
              />
            ) : (
              <OngoingBulkSearchTable
                voyageData={voyageData}
                vessels={vessels}
                onDataChange={handleVoyageDataChange}
                onSearch={handleSearch}
                isSearching={isLoading}
                hasSearched={hasSearched}
              />
            )}
          </Box>
        </>
      )}
    </Paper>
  );
};

OngoingBulkSearchSection.propTypes = {
  onSearchResults: PropTypes.func.isRequired,
  voyageData: PropTypes.arrayOf(PropTypes.object),
  onDataChange: PropTypes.func,
  displayMode: PropTypes.oneOf(['cards', 'table']),
  onDisplayModeChange: PropTypes.func
};

export default OngoingBulkSearchSection;
